import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { 
  FileText, TrendingUp, Flame, Coins, Plus, ArrowRight, 
  Trophy, Target, Sparkles 
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Dashboard = ({ onNavigate }) => {
  const { getAuthHeaders, subscription } = useAuth();
  const [analytics, setAnalytics] = useState(null);
  const [recentArticles, setRecentArticles] = useState([]);
  const [achievements, setAchievements] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      const [analyticsRes, articlesRes, achievementsRes] = await Promise.all([
        axios.get(`${API}/analytics/dashboard`, { headers: getAuthHeaders() }),
        axios.get(`${API}/articles?limit=5`, { headers: getAuthHeaders() }),
        axios.get(`${API}/achievements`, { headers: getAuthHeaders() })
      ]);
      
      setAnalytics(analyticsRes.data);
      setRecentArticles(articlesRes.data.slice(0, 5));
      setAchievements(achievementsRes.data);
    } catch (error) {
      console.error('Failed to fetch dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner"></div>
      </div>
    );
  }

  const stats = [
    {
      label: 'Articles',
      value: analytics?.articles?.total || 0,
      subLabel: `${analytics?.articles?.published || 0} published`,
      icon: FileText,
      color: '#E8735C'
    },
    {
      label: 'Avg SEO Score',
      value: analytics?.seo?.average_score || 0,
      subLabel: 'across all articles',
      icon: Target,
      color: 'var(--color-charcoal)'
    },
    {
      label: 'Current Streak',
      value: analytics?.streak?.current || 0,
      subLabel: `Best: ${analytics?.streak?.longest || 0} days`,
      icon: Flame,
      color: '#E8735C'
    },
    {
      label: 'Credits',
      value: analytics?.credits?.remaining?.toFixed(1) || 0,
      subLabel: `${analytics?.credits?.used?.toFixed(1) || 0} used`,
      icon: Coins,
      color: 'var(--color-charcoal)'
    }
  ];

  const unlockedAchievements = achievements?.achievements?.filter(a => a.unlocked) || [];

  return (
    <div className="fade-in" data-testid="dashboard">
      {/* Header */}
      <div className="flex justify-between items-start mb-8">
        <div>
          <h1 className="font-serif text-3xl mb-1">Dashboard</h1>
          <p className="text-sm text-[var(--color-muted)]">Build your content pillars. Become the source AI trusts.</p>
        </div>
        <button 
          onClick={() => onNavigate('articles')}
          className="btn btn-primary"
          data-testid="create-article-btn"
        >
          <Plus className="w-4 h-4" />
          Create article
        </button>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        {stats.map((stat, idx) => {
          const Icon = stat.icon;
          return (
            <div key={idx} className="stat-card">
              <div className="flex items-start justify-between mb-4">
                <div className="w-9 h-9 rounded-xl flex items-center justify-center bg-[var(--color-surface)]">
                  <Icon className="w-4 h-4" style={{color: stat.color}} />
                </div>
              </div>
              <div className="stat-value">{stat.value}</div>
              <div className="stat-label">{stat.label}</div>
              <p className="text-[11px] text-[var(--color-muted)] mt-1">{stat.subLabel}</p>
            </div>
          );
        })}
      </div>

      {/* Main Content Grid */}
      <div className="grid lg:grid-cols-3 gap-5">
        {/* Recent Articles */}
        <div className="lg:col-span-2">
          <div className="card p-6">
            <div className="flex items-center justify-between mb-5">
              <h2 className="font-serif text-xl">Recent Articles</h2>
              <button 
                onClick={() => onNavigate('articles')}
                className="text-xs font-medium flex items-center gap-1 text-[var(--color-coral)] hover:underline"
              >
                View all <ArrowRight className="w-3 h-3" />
              </button>
            </div>
            
            {recentArticles.length === 0 ? (
              <div className="text-center py-14">
                <div className="w-14 h-14 mx-auto rounded-full flex items-center justify-center mb-4 bg-[var(--color-surface)]">
                  <Sparkles className="w-7 h-7 text-[var(--color-coral)]" />
                </div>
                <h3 className="font-display font-medium text-sm mb-2">No articles yet</h3>
                <p className="text-xs text-[var(--color-muted)] mb-5">
                  Create your first content pillar and start building authority.
                </p>
                <button 
                  onClick={() => onNavigate('articles')}
                  className="btn btn-primary text-sm"
                >
                  Create first article
                </button>
              </div>
            ) : (
              <div className="space-y-3">
                {recentArticles.map((article) => (
                  <div key={article.id} className="article-card">
                    <div className="article-card-content">
                      <h3 className="article-title">{article.title || 'Untitled'}</h3>
                      <div className="article-meta">
                        <span className={`badge badge-${article.status}`}>
                          {article.status}
                        </span>
                        <span>SEO: {article.seo_score || 0}</span>
                        <span>{new Date(article.created_at).toLocaleDateString()}</span>
                      </div>
                    </div>
                    <div 
                      className={`seo-score ${(article.seo_score || 0) >= 70 ? 'excellent' : (article.seo_score || 0) >= 50 ? 'good' : 'poor'}`}
                      style={{'--score': `${(article.seo_score || 0) * 3.6}deg`}}
                    >
                      <span>{article.seo_score || 0}</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Sidebar */}
        <div className="space-y-5">
          {/* Quick Actions */}
          <div className="card p-6">
            <h2 className="font-serif text-xl mb-4">Quick Actions</h2>
            <div className="space-y-2">
              <button 
                onClick={() => onNavigate('articles')}
                className="btn btn-secondary w-full justify-start text-sm"
              >
                <FileText className="w-4 h-4" />
                New Article
              </button>
              <button 
                onClick={() => onNavigate('voice')}
                className="btn btn-secondary w-full justify-start text-sm"
                disabled={!subscription?.voice_enabled}
              >
                <Sparkles className="w-4 h-4" />
                Voice to Article
                {!subscription?.voice_enabled && (
                  <span className="ml-auto text-[10px] px-1.5 py-0.5 rounded-full bg-[rgba(232,115,92,0.08)] text-[var(--color-coral)] font-semibold">
                    Pro
                  </span>
                )}
              </button>
              <button 
                onClick={() => onNavigate('assistant')}
                className="btn btn-secondary w-full justify-start text-sm"
                disabled={!subscription?.ai_assistant}
              >
                <TrendingUp className="w-4 h-4" />
                Content Strategy
                {!subscription?.ai_assistant && (
                  <span className="ml-auto text-[10px] px-1.5 py-0.5 rounded-full bg-[rgba(232,115,92,0.08)] text-[var(--color-coral)] font-semibold">
                    Pro
                  </span>
                )}
              </button>
            </div>
          </div>

          {/* Achievements */}
          <div className="card p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-serif text-xl">Achievements</h2>
              <Trophy className="w-4 h-4 text-[var(--color-charcoal)]" />
            </div>
            
            {unlockedAchievements.length === 0 ? (
              <p className="text-xs text-[var(--color-muted)] text-center py-4">
                Publish your first article to unlock achievements!
              </p>
            ) : (
              <div className="grid grid-cols-3 gap-2">
                {unlockedAchievements.slice(0, 6).map((achievement) => (
                  <div key={achievement.id} className="achievement">
                    <div className="achievement-icon">
                      <Trophy className="w-4 h-4" />
                    </div>
                    <span className="achievement-name">{achievement.name}</span>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Tip of the Day */}
          <div className="card p-6 bg-[var(--color-surface)]" style={{boxShadow: 'none'}}>
            <h3 className="font-display text-xs font-medium mb-2 text-[var(--color-charcoal)]">Tip of the Day</h3>
            <p className="text-xs text-[var(--color-muted)] leading-relaxed">
              Consistency beats perfection. Publishing 2 articles per week builds more authority than one "perfect" article per month.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
